<template>
    <KvModal ref="clientModal" @hide="resetErrors">
      <template #title>{{ isCreating ? t('clients-modals-new_contact') : t('clients-modals-edit_data') }}</template>
      <template #body>
        <KvCard collapsible default-open density="compact" variant="tonal">
          <template #title>
            <KvTypography weight="semibold" size="large" color="secondary">
              {{ t('clients-modals-main_info') }}
            </KvTypography>
          </template>
          <template #text>
            <div class="fields-grid gap-md">
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-modals-integration_id') }}</KvTypography>
                <KvInput v-model="localClient.external_id" size="large" />
              </div>
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-modals-first_name') }}</KvTypography>
                <KvInput v-model="localClient.first_name" size="large" />
              </div>
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-basic-last_name') }}</KvTypography>
                <KvInput v-model="localClient.last_name" size="large" />
              </div>
            </div>
            <div class="fields-grid gap-md mt-4">
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-modals-mobile') }}</KvTypography>
                <KvInput v-model="localClient.whatsapp" size="large" type="tel" v-mask="'(##) #####-####'" :error="!!errors.phone" />
                <KvTypography v-if="errors.phone" color="error" size="extra-small" weight="medium">
                  {{ errors.phone === 'invalid' ? t('clientForm-errors-invalid_phone') : t('clientForm-errors-duplicate_phone') }}
                </KvTypography>
              </div>
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-columns-email') }}</KvTypography>
                <KvInput v-model="localClient.email" size="large" :error="!!errors.email" />
                <KvTypography v-if="errors.email" color="error" size="extra-small" weight="medium">
                  {{ errors.email === 'invalid' ? t('clientForm-errors-invalid_email') : t('clientForm-errors-duplicate_email') }}
                </KvTypography>
              </div>
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvTypography color="primary" size="medium" weight="medium">{{ t('clients-basic-birthdate') }}</KvTypography>
                <KvDatePicker v-model="localClient.birthdate" size="large" :placeholder="t('clients-modals-birthdate_placeholder')" :error="!!errors.birthdate" />
                <KvTypography v-if="errors.birthdate" color="error" size="extra-small" weight="medium">
                  {{ t('clientForm-errors-invalid_birthdate') }}
                </KvTypography>
              </div>
            </div>
          </template>
        </KvCard>

        <KvCard collapsible default-open density="compact" variant="tonal" v-if="$module('client_fields')" class="mt-4" v-for="custom_field in localClient.custom_fields" :key="custom_field.group_id">
          <template #title>
            <KvTypography weight="semibold" size="large" color="secondary">
              <!-- i18n-validator-ignore-next-line: runtime custom field group name -->
              {{ custom_field.group_name }}
            </KvTypography>
          </template>
          <template #text>
            <div class="fields-grid gap-md">
              <KvCustomField v-for="field in custom_field.fields" :key="field.id" :fieldName="field.field_name" :field="field" :type="field.field_type" v-model="field.value" size="large" /><!-- i18n-validator-ignore-next-line: runtime custom field label -->
            </div>
          </template>
        </KvCard>
      </template>

      <template #footer="{ toggle }">
        <div class="d-flex align-items-center w-100 justify-content-end gap-sm">
          <KvButton variant="outlined" @click="toggle">{{ t('clients-modals-cancel') }}</KvButton>
          <KvButton variant="filled" :prepend-icon="isCreating ? 'ui-plus' : 'ui-check'" @click="saveClient(toggle)" :loading="processing.client">
            {{ isCreating ? t('clients-modals-create_contact') : t('clients-basic-save') }}
          </KvButton>
        </div>
      </template>
    </KvModal>
</template>
<script>
import httpStatus from "@/helpers/httpStatus";
import keviApi from "@/keviApi";
import KvModal from "@/components/modal/modal.vue";
import KvTypography from "@/components/ui/kv-typography.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvDatePicker from "@/components/inputs/kv-date-picker.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvCard from "@/components/ui/kv-card.vue";
import KvCustomField from "@/components/custom-fields/kv-custom-field.vue";

export default {
  components: { KvModal, KvTypography, KvInput, KvDatePicker, KvButton, KvCard, KvCustomField },
  props: {
    clientId: { type: [String, Number], default: null },
    client: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isCreating: false,
      processing: { client: false },
      errors: { email: null, phone: null, birthdate: null },
      localClient: {},
    };
  },

  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    openClient() {
      this.isCreating = false;
      this.resetErrors();
      this.localClient = this.client ? JSON.parse(JSON.stringify(this.client)) : {};
      this.$refs.clientModal.toggleShow();
    },
    async openCreateClient() {
      this.isCreating = true;
      this.resetErrors();
      this.localClient = {
        external_id: null,
        first_name: null,
        last_name: null,
        whatsapp: null,
        email: null,
        birthdate: null,
        subscribed_sms: true,
        subscribed_whatsapp: true,
        subscribed_email: true,
        subscribed_rcs: true,
        custom_fields: [],
      };

      if (this.$module('client_fields')) {
        try {
          const options = keviApi.getOptions("grouped/client-fields", "GET");
          const response = await this.$http(options);
          if (response.body && Array.isArray(response.body)) {
            this.localClient.custom_fields = response.body.map(group => ({
              group_id: group.id,
              group_name: group.name,
              fields: group.fields.map(field => ({
                ...field,
                value: ""
              }))
            }));
          }
        } catch (e) {
          console.error("Failed to load client custom fields:", e);
        }
      }

      this.$refs.clientModal.toggleShow();
    },
    resetErrors() {
      this.errors = { email: null, phone: null, birthdate: null };
    },
    getCustomFields() {
      if (!this.localClient?.custom_fields) return {};

      return this.localClient.custom_fields.reduce((acc, group) => {
        if (group.fields) {
          const fields = Array.isArray(group.fields) ? group.fields : Object.values(group.fields);

          fields.forEach(field => {
            acc[field.id] = field.value;
          });
        }
        return acc;
      }, {});
    },
    buildPayload() {
      const customFields = this.getCustomFields();
      const payload = {
        custom_fields: customFields,
        email: this.localClient.email,
        external_id: this.localClient.external_id,
        first_name: this.localClient.first_name,
        last_name: this.localClient.last_name,
        whatsapp: this.localClient.whatsapp,
        subscribed_sms: this.localClient.subscribed_sms,
        subscribed_whatsapp: this.localClient.subscribed_whatsapp,
        subscribed_email: this.localClient.subscribed_email,
        subscribed_rcs: this.localClient.subscribed_rcs,
      };

      if (this.localClient.birthdate) {
        const dateObj = new Date(this.localClient.birthdate);
        if (!isNaN(dateObj.getTime())) {
          payload.birthdate = [
            String(dateObj.getDate()).padStart(2, '0'),
            String(dateObj.getMonth() + 1).padStart(2, '0'),
            dateObj.getFullYear()
          ].join('/');
        }
      }

      return payload;
    },
    async saveClient(toggle) {
      this.processing.client = true;
      this.resetErrors();

      try {
        const payload = this.buildPayload();

        let url, method;
        if (this.isCreating) {
          url = "client/new";
          method = "PUT";
        } else {
          url = `client/${this.clientId}/edit`;
          method = "POST";
        }

        const options = keviApi.getOptions(url, method, { ...payload });
        const response = await this.$http(options);

        if (response.status === httpStatus.CREATED || response.status === httpStatus.OK) {
          if (this.isCreating) {
            const newClient = response.body.client;
            this.$emit("client-created", newClient);
            window.mixpanel?.track("Client saved", newClient);
            this.$store.dispatch('notification/success', this.t('clients-modals-client_created_success'));
          } else {
            this.$emit("refresh-client", this.localClient);
            this.$store.dispatch('notification/success', this.t('clients-modals-client_updated_success'));
          }
          toggle();
        } else if (response.body?.errors) {
          this.errors = {
            email: response.body.errors.email || null,
            phone: response.body.errors.phone || null,
            birthdate: response.body.errors.birthdate || null,
          };
        }
      } catch (error) {
        if (error?.body?.errors) {
          this.errors = {
            email: error.body.errors.email || null,
            phone: error.body.errors.phone || null,
            birthdate: error.body.errors.birthdate || null,
          };
        }
      } finally {
        this.processing.client = false;
      }
    }
  },
  watch: {
    client: {
      immediate: true,
      deep: true,
      handler(newClient) {
        if (newClient && !this.isCreating) {
          this.localClient = JSON.parse(JSON.stringify(newClient));
        } else if (!this.isCreating) {
          this.localClient = {};
        }
      }
    }
  }
}

</script>

<style lang="less" scoped>
.fields-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}
</style>